import { useMutation } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import { DSCheckbox } from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms";
import { useOrganization, useTemplates } from "@hooks";
import { useState, useEffect } from "react";

import TeamDefaultModalExplanationCard from "./TeamDefaultModalExplanationCard";
import {
  SET_TEAM_DEFAULT_TEMPLATE,
  REMOVE_TEAM_DEFAULT_TEMPLATE,
  SET_ORGANIZATION_DEFAULT_TEMPLATE,
  REMOVE_ORGANIZATION_DEFAULT_TEMPLATE,
} from "../../../graphql/mutations/AutoTemplates";
import { trackOperation } from "../../../utils/operationTracking";

const TeamDefaultModal = ({
  template,
  adminTeams,
  onClose,
  onSuccess,
  source,
}) => {
  const { isOrgAdmin, organization, orgUuid } = useOrganization();
  const { refetchDefaultTemplates, defaultTemplatesForSelectedOrg } =
    useTemplates();

  const [selectedTeams, setSelectedTeams] = useState(
    new Set(template?.teamDefaults || []),
  );
  const [loading, setLoading] = useState(false);

  const [setTeamDefault] = useMutation(SET_TEAM_DEFAULT_TEMPLATE);
  const [removeTeamDefault] = useMutation(
    REMOVE_TEAM_DEFAULT_TEMPLATE,
  );
  const [setOrgDefault] = useMutation(
    SET_ORGANIZATION_DEFAULT_TEMPLATE,
  );
  const [removeOrgDefault] = useMutation(
    REMOVE_ORGANIZATION_DEFAULT_TEMPLATE,
  );

  let canSetOrgDefault = false;
  if (
    template?.sharingType === "TEAM" &&
    organization &&
    isOrgAdmin
  ) {
    const templateOwnerTeams = template?.user?.teams || [];
    const ownerOrgUuids = templateOwnerTeams
      .map((team) => team.organization?.uuid)
      .filter(Boolean);
    canSetOrgDefault = ownerOrgUuids?.includes(orgUuid);
  }

  const isOrgDefault = !!defaultTemplatesForSelectedOrg?.some(
    (t) => t.uuid === template?.uuid,
  );

  const [isOrgDefaultChecked, setIsOrgDefaultChecked] =
    useState(isOrgDefault);

  let eligibleTeams = adminTeams;
  if (template?.user?.uuid) {
    const templateOwnerUuid = template.user.uuid;
    eligibleTeams = adminTeams.filter((team) =>
      team.users?.some((user) => user.uuid === templateOwnerUuid),
    );
  }

  useEffect(() => {
    // If org default is set, all eligible teams should be checked
    // Otherwise, use the team defaults from the template
    if (isOrgDefault) {
      setSelectedTeams(
        new Set(eligibleTeams.map((team) => team.uuid)),
      );
    } else {
      setSelectedTeams(new Set(template?.teamDefaults || []));
    }
  }, [template?.teamDefaults, isOrgDefault, eligibleTeams]);

  useEffect(() => {
    setIsOrgDefaultChecked(isOrgDefault);
  }, [isOrgDefault]);

  const handleToggleTeam = (teamUuid) => {
    setSelectedTeams((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(teamUuid)) {
        newSet.delete(teamUuid);
      } else {
        newSet.add(teamUuid);
      }
      return newSet;
    });
  };

  const handleOrgDefaultChange = (e) => {
    const newValue = e.target.checked;
    setIsOrgDefaultChecked(newValue);
    if (newValue) {
      // When enabling org default, clear individual team selections
      setSelectedTeams(new Set());
    } else {
      // When disabling org default, restore original team defaults
      setSelectedTeams(new Set(template?.teamDefaults || []));
    }
  };

  const handleOrgDefaultToggle = () =>
    handleOrgDefaultChange({
      target: { checked: !isOrgDefaultChecked },
    });

  const handleSave = () =>
    trackOperation(
      "template-default-settings-updated",
      async (ctx) => {
        setLoading(true);
        // If user is not org admin and template is org default, don't allow any changes
        if (!isOrgAdmin && isOrgDefault) {
          const allEligibleTeamUuids = new Set(
            eligibleTeams.map((team) => team.uuid),
          );
          const hasChanges =
            selectedTeams.size !== allEligibleTeamUuids.size ||
            [...selectedTeams].some(
              (uuid) => !allEligibleTeamUuids.has(uuid),
            ) ||
            [...allEligibleTeamUuids].some(
              (uuid) => !selectedTeams.has(uuid),
            );

          if (hasChanges) {
            setLoading(false);
            throw new Error(
              "This template has been set as a default by an admin. You cannot modify team defaults.",
            );
          }
          onClose();
          return;
        }

        const beforeTeamDefaults = template?.teamDefaults || [];
        const afterTeamDefaults = isOrgDefaultChecked
          ? []
          : [...selectedTeams];

        ctx.addProperties({
          templateUuid: template.uuid,
          templateOwnerUuid: template.user?.uuid,
          beforeTeamDefaults,
          afterTeamDefaults,
          beforeOrgDefault: isOrgDefault ? orgUuid : null,
          afterOrgDefault: isOrgDefaultChecked ? orgUuid : null,
          source,
        });

        // Handle org default changes
        if (
          canSetOrgDefault &&
          isOrgDefaultChecked !== isOrgDefault
        ) {
          if (isOrgDefaultChecked) {
            await setOrgDefault({
              variables: {
                templateUuid: template.uuid,
                orgUuid: orgUuid,
              },
            });
          } else {
            await removeOrgDefault({
              variables: {
                templateUuid: template.uuid,
                orgUuid: orgUuid,
              },
            });
          }
          refetchDefaultTemplates();
        }

        // Handle team default changes (if org default is not checked)
        if (!isOrgDefaultChecked) {
          const currentDefaults = new Set(
            template?.teamDefaults || [],
          );
          const teamsToAdd = [...selectedTeams].filter(
            (uuid) => !currentDefaults.has(uuid),
          );
          const teamsToRemove = [...currentDefaults].filter(
            (uuid) => !selectedTeams.has(uuid),
          );

          if (teamsToAdd.length > 0) {
            await setTeamDefault({
              variables: {
                templateUuid: template.uuid,
                teamUuids: teamsToAdd,
              },
            });
          }

          if (teamsToRemove.length > 0) {
            await removeTeamDefault({
              variables: {
                templateUuid: template.uuid,
                teamUuids: teamsToRemove,
              },
            });
          }
        } else {
          // If org default is checked, remove all team defaults
          const currentDefaults = new Set(
            template?.teamDefaults || [],
          );
          if (currentDefaults.size > 0) {
            await removeTeamDefault({
              variables: {
                templateUuid: template.uuid,
                teamUuids: [...currentDefaults],
              },
            });
          }
        }

        alert(
          "success",
          "Team default settings updated successfully.",
        );
        setLoading(false);
        onSuccess();
        onClose();
      },
    );

  return (
    <DSModal
      isOpen={true}
      onClose={onClose}
      title="Set as a Team Default"
      actions={[
        {
          label: "Cancel",
          variant: "cancel",
          onClick: onClose,
          disabled: loading,
        },
        {
          label: loading ? "Saving..." : "Save",
          variant: "primary",
          onClick: handleSave,
          disabled:
            loading ||
            (eligibleTeams.length === 0 && !canSetOrgDefault),
          dataTestId: "confirm-button",
        },
      ]}
      dataTestId="team-default-modal"
    >
      <div className="p-md">
        <p className="text-sm text-secondary mb-md">
          Select which teams should have this template as a default.
          Team members will see it in their "Team Defaults" section.
        </p>

        {canSetOrgDefault && (
          <div className="mb-md">
            <div
              className="flex items-center p-sm rounded-standard border border-secondary hover:bg-secondary cursor-pointer transition-colors"
              onClick={handleOrgDefaultToggle}
            >
              <div
                className="contents"
                onClick={(e) => e.stopPropagation()}
              >
                <DSCheckbox
                  checked={isOrgDefaultChecked}
                  onChange={handleOrgDefaultChange}
                  dataTestId="org-default-checkbox"
                  aria-label="Set as default for all teams"
                />
              </div>
              <span className="text-sm font-medium grow ml-sm">
                Set as default for all teams
              </span>
            </div>
          </div>
        )}

        <div className="space-y-2 mb-md">
          {eligibleTeams.map((team) => {
            const isTeamDefault = selectedTeams.has(team.uuid);
            const isSelected = isOrgAdmin
              ? isTeamDefault || isOrgDefaultChecked
              : isTeamDefault || isOrgDefault || isOrgDefaultChecked;
            const isDisabled =
              isOrgDefaultChecked ||
              (!isOrgAdmin && isOrgDefault && isSelected);

            return (
              <div
                key={team.uuid}
                className={`flex items-center p-sm rounded-standard border border-secondary transition-colors ${
                  isDisabled
                    ? "opacity-50 cursor-not-allowed bg-secondary"
                    : "hover:bg-secondary cursor-pointer"
                }`}
                onClick={() =>
                  !isDisabled && handleToggleTeam(team.uuid)
                }
              >
                <div
                  className="contents"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DSCheckbox
                    checked={isSelected}
                    onChange={() => handleToggleTeam(team.uuid)}
                    disabled={isDisabled}
                    dataTestId={`team-default-checkbox-${team.uuid}`}
                    aria-label={`Set ${team.name} as default`}
                  />
                </div>
                <span className="text-sm grow ml-sm">
                  {team.name}
                </span>
              </div>
            );
          })}
        </div>

        {eligibleTeams.length === 0 && (
          <div className="text-center py-4 text-secondary">
            <p className="text-sm mb-sm">
              You cannot set this template as a default for any of
              your teams.
            </p>
            <p className="text-xs">
              You can only set this as a default for teams that the
              template owner is on. Try making a copy if you want to
              share to more teams.
            </p>
          </div>
        )}

        {!isOrgAdmin && isOrgDefault && eligibleTeams.length > 0 && (
          <TeamDefaultModalExplanationCard
            title="Wondering why you can't uncheck a template?"
            message="An admin has defaulted this template for all teams. Team default settings cannot be modified."
          />
        )}

        {eligibleTeams.length > 0 &&
          eligibleTeams.length < adminTeams.length && (
            <TeamDefaultModalExplanationCard
              title="Wondering why you don't see all of your teams here?"
              message="Only teams that the template owner is on are shown. To set this as a default for all your teams, use the 'Set as default for all teams' option above, or make a copy of this template."
            />
          )}
      </div>
    </DSModal>
  );
};

export default TeamDefaultModal;
